import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, SearchableSelect } from '@/backoffice/components/ui';
import { formatCurrency } from '@/backoffice/utils';
import { fetchDriverZones, type DriverZonesData } from '@/domains/driver/services/driver.service';
import { getSession } from '@/domains/auth/services/auth.service';
import { syncUserCoverageZones, detachUserCoverageZones } from '@/domains/api/user-relations.api';
import { MapPin, Clock, TrendingUp, Plus, X, Loader2 } from 'lucide-react';

export function ProZonesPage() {
  const [zonesData, setZonesData] = useState<DriverZonesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedZone, setSelectedZone] = useState('');

  const load = () => {
    setLoading(true);
    fetchDriverZones()
      .then(setZonesData)
      .catch(() => setZonesData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const activeIds = new Set(zonesData?.active_zones.map((z) => z.id) ?? []);

  const handleAddZone = async (zoneId?: number) => {
    const id = zoneId ?? Number(selectedZone);
    if (!id || activeIds.has(id)) return;

    const userId = getSession()?.user?.id;
    if (!userId) return;

    setSaving(true);
    try {
      await syncUserCoverageZones(userId, [id]);
      setSelectedZone('');
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveZone = async (zoneId: number) => {
    const userId = getSession()?.user?.id;
    if (!userId) return;

    setSaving(true);
    try {
      await detachUserCoverageZones(userId, [zoneId]);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="pro_driver" title="Mes zones">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const activeZones = zonesData?.active_zones ?? [];
  const availableZones = zonesData?.available_zones ?? [];

  return (
    <DashboardLayout userType="pro_driver" title="Mes zones">
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Zones actives ({activeZones.length})</h3>
        </div>

        {activeZones.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune zone active. Ajoutez une zone ci-dessous.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeZones.map((zone) => (
              <div key={zone.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-blue-900">{zone.name}</p>
                    <p className="text-sm text-blue-700 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4" />
                      Zone de couverture
                    </p>
                  </div>
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-blue-100"
                    onClick={() => handleRemoveZone(zone.id)}
                    disabled={saving}
                  >
                    <X className="w-4 h-4 text-blue-600" />
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1 text-blue-700">
                    <TrendingUp className="w-4 h-4" />
                    <span>{zone.deliveries} livraisons</span>
                  </div>
                  <div className="text-blue-700">{formatCurrency(zone.revenue)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Performance par zone</h3>
        {activeZones.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune donnée de performance.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Zone</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Livraisons</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Revenus</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeZones.map((zone) => (
                  <tr key={zone.id}>
                    <td className="py-3 font-medium text-gray-900">{zone.name}</td>
                    <td className="py-3 text-right">{zone.deliveries}</td>
                    <td className="py-3 text-right font-medium text-emerald-600">{formatCurrency(zone.revenue)}</td>
                    <td className="py-3 text-right">{zone.rating}/5</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h3 className="font-semibold text-gray-900">Ajouter une zone</h3>
          <div className="flex gap-2 w-full sm:w-auto">
            <SearchableSelect
              options={availableZones
                .filter((z) => !activeIds.has(z.id))
                .map((z) => ({ value: String(z.id), label: z.name }))}
              value={selectedZone}
              onChange={setSelectedZone}
              placeholder="Rechercher une zone..."
              className="flex-1 sm:w-64"
            />
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={handleAddZone}
              isLoading={saving}
              disabled={!selectedZone}
            >
              Ajouter
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {availableZones.map((zone) => {
            const isActive = activeIds.has(zone.id);
            return (
              <div
                key={zone.id}
                className={`p-4 rounded-lg border ${
                  isActive ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-900">{zone.name}</p>
                  {isActive && <Badge variant="success" size="sm">Active</Badge>}
                </div>
                {!isActive && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => handleAddZone(zone.id)}
                    disabled={saving}
                  >
                    Ajouter
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </DashboardLayout>
  );
}
